/**
 * `PageDetailLayout` — the shared frame for ~12 detail pages (agents, sessions, tools, memory,
 * connectors, schedules, workflows, MCP servers, external systems, tasks, tenants).
 *
 * The **public shape has not changed** — every consumer still passes `title`, `subtitle`,
 * `breadcrumbs`, `backTo`, `status`, `chips`, `actions`, `tabs`, `loading`, `error`,
 * `onErrorDismiss`, `icon`, `headerExtra`. The MUI-based glassmorphism internals have been swapped
 * out for `@coordio/ui`'s detail-page primitives (`PageDetailHeader`, `PageContainer`,
 * `LoadingSkeleton`, `InlineErrorPanel`, `StatusBadge`, `Chip`, `Button`, `Tooltip`) so every
 * detail page inherits the CoOrdio Cortex shell chrome — the same header, the same tab strip, the
 * same design tokens — without needing to be touched.
 *
 * ─── Why this file and not per-page migrations ─────────────────────────────────────────────────
 * The list-page migration was done page-by-page (see `ProjectListPage.tsx` and the 26 sister list
 * pages) because each list page owns its own toolbar, column plan and filters. Detail pages own
 * their inner tabs but share this exact outer chrome verbatim — so one file changed here means
 * twelve pages migrated. Any per-page detail work (e.g. rebuilding a tab body to use
 * `DescriptionList` instead of a hand-rolled `Box`-based key/value grid) can happen later without
 * touching this file.
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 *
 * ─── Chip / status colour mapping ──────────────────────────────────────────────────────────────
 * MUI's palette (`success` | `warning` | `error` | `info` | `default` | `primary` | `secondary`)
 * does not map 1:1 onto `@coordio/ui`'s `ChipColor` (`red` | `violet` | `green` | `pink` | `blue`
 * | `amber` | `accent` | `default`). The mapping below is the smallest one that keeps every
 * existing chip visually correct on migration:
 *   success → green   warning → amber   error → red     info → blue
 *   primary → accent  secondary → violet  default → default
 * Status pills go through `StatusBadge` instead, which takes a semantic `StatusColor` and picks
 * the right token colour itself.
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 */

import { useEffect, useState, type ReactNode, type SyntheticEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, X } from 'lucide-react';

import {
  Button,
  Chip,
  type ChipColor,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@coordio/ui';
import type { StatusColor } from '@coordio/foundation';
import {
  InlineErrorPanel,
  LoadingSkeleton,
  PageContainer,
  PageDetailHeader,
  StatusBadge,
} from '@coordio/ui/layout';

import './PageDetailLayout.scss';

// ─── Public API — do not change without a migration of every consumer ─────────────────────────────

interface BreadcrumbItem {
  label: string;
  href?: string;
}

/**
 * Accepts both the original MUI palette names AND the `@coordio/ui` `ChipColor` names so
 * already-migrated pages (e.g. McpServerDetailPage) that pass `'green'` / `'red'` / `'amber'`
 * compile without an adapter, while legacy pages that still pass `'success'` / `'error'` etc.
 * continue to work through {@link mapMuiStatusColor}.
 */
interface StatusConfig {
  label: string;
  color: 'success' | 'warning' | 'error' | 'info' | 'default' | 'primary' | 'secondary' | ChipColor;
}

interface ChipConfig {
  label: string;
  color?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | ChipColor;
  variant?: 'filled' | 'outlined';
}

/**
 * Action button descriptor. `variant` is the MUI vocabulary; mapped at render time to
 * `@coordio/ui`'s Button variants (`default` | `secondary` | `ghost` | `outline` | `destructive`).
 * Actions without `variant` (or `variant: 'text'`) become icon-only ghost buttons wrapped in a
 * tooltip — same UX as the pre-migration icon-in-`IconButton` pattern.
 */
interface ActionConfig {
  icon?: ReactNode;
  label: string;
  onClick: () => void;
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' | 'inherit';
  variant?: 'contained' | 'outlined' | 'text';
  disabled?: boolean;
}

interface TabConfig {
  label: string;
  value: string;
  content: ReactNode;
  disabled?: boolean;
}

interface PageDetailLayoutProps {
  /** Page title (entity name). */
  title: string;
  /** Subtitle or description. */
  subtitle?: string;
  /** Breadcrumb navigation. */
  breadcrumbs?: BreadcrumbItem[];
  /** Back navigation config. */
  backTo?: { label: string; href: string };
  /** Status indicator. */
  status?: StatusConfig;
  /** Metadata chips. */
  chips?: ChipConfig[];
  /** Action buttons. */
  actions?: ActionConfig[];
  /** Tabbed content. */
  tabs?: TabConfig[];
  /** Default tab value. */
  defaultTab?: string;
  /** Content (used when tabs are not provided). */
  children?: ReactNode;
  /** Loading state. */
  loading?: boolean;
  /** Error message. */
  error?: string;
  /** Error dismiss handler. */
  onErrorDismiss?: () => void;
  /** Icon for the entity. */
  icon?: ReactNode;
  /** Extra header content (rendered below the meta row). */
  headerExtra?: ReactNode;
  /** Test ID forwarded to the root element for E2E selectors. */
  'data-testid'?: string;
}

// ─── MUI → Cortex vocabulary bridges ──────────────────────────────────────────────────────────────

/** MUI palette → `ChipColor`. Passes through values that are already valid `ChipColor` names. */
function mapMuiChipColor(color?: ChipConfig['color']): ChipColor {
  switch (color) {
    case 'success':
      return 'green';
    case 'warning':
      return 'amber';
    case 'error':
      return 'red';
    case 'info':
      return 'blue';
    case 'primary':
      return 'accent';
    case 'secondary':
      return 'violet';
    case 'red':
    case 'violet':
    case 'green':
    case 'pink':
    case 'blue':
    case 'amber':
    case 'accent':
      return color;
    default:
      return 'default';
  }
}

/** MUI palette → `StatusColor`. Passes through `ChipColor` names mapped to nearest semantic peer. */
function mapMuiStatusColor(color: StatusConfig['color']): StatusColor {
  switch (color) {
    case 'success':
    case 'green':
      return 'success';
    case 'warning':
    case 'amber':
      return 'warning';
    case 'error':
    case 'red':
      return 'error';
    case 'info':
    case 'blue':
      return 'info';
    default:
      return 'default';
  }
}

/**
 * MUI Button variant → `@coordio/ui` variant. `contained` == default filled, `outlined` == outline,
 * anything else (including `text` and undefined) becomes an icon-only `ghost` button.
 */
function mapMuiButtonVariant(
  variant: ActionConfig['variant'],
  color: ActionConfig['color'],
): 'default' | 'outline' | 'destructive' | 'ghost' | 'secondary' {
  if (color === 'error') return 'destructive';
  if (variant === 'contained') return 'default';
  if (variant === 'outlined') return 'outline';
  return 'ghost';
}

// ─── Component ────────────────────────────────────────────────────────────────────────────────────

export const PageDetailLayout = ({
  title,
  subtitle,
  breadcrumbs,
  backTo,
  status,
  chips,
  actions,
  tabs,
  defaultTab,
  children,
  loading = false,
  error,
  onErrorDismiss,
  icon,
  headerExtra,
  'data-testid': dataTestId,
}: PageDetailLayoutProps) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>(defaultTab || (tabs?.[0]?.value ?? ''));

  // Detail pages commonly render `tabs` conditionally (only after data loads), so on first mount
  // `tabs` may be empty and `activeTab` gets stuck at "". Re-sync once tabs become available, or
  // whenever the current tab is no longer in the list.
  useEffect(() => {
    if (!tabs || tabs.length === 0) return;
    const isValid = tabs.some((t) => t.value === activeTab);
    if (!isValid) {
      const fallback =
        (defaultTab && tabs.find((t) => t.value === defaultTab)?.value) || tabs[0].value;
      setActiveTab(fallback);
    }
  }, [tabs, defaultTab, activeTab]);

  const handleTabChange = (value: string) => (_: SyntheticEvent) => setActiveTab(value);

  // ─── Leading slot: back button + breadcrumbs ─────────────────────────────────────────────────
  // `PageDetailHeader` has one `leading` slot and no built-in breadcrumbs, so both go here. Nothing
  // is rendered when neither is set, so pages that pass only `title` still get a plain header.
  const leading = backTo || (breadcrumbs && breadcrumbs.length > 0) ? (
    <div className="page-detail-layout__leading">
      {backTo && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="page-detail-layout__back"
          onClick={() => navigate(backTo.href)}
          aria-label={`Back to ${backTo.label}`}
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
        </Button>
      )}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav className="page-detail-layout__breadcrumbs" aria-label="Breadcrumb">
          <ol>
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;
              return (
                <li key={crumb.label} className="page-detail-layout__crumb">
                  {isLast || !crumb.href ? (
                    <span aria-current={isLast ? 'page' : undefined}>{crumb.label}</span>
                  ) : (
                    <button
                      type="button"
                      className="page-detail-layout__crumb-link"
                      onClick={() => crumb.href && navigate(crumb.href)}
                    >
                      {crumb.label}
                    </button>
                  )}
                  {!isLast && (
                    <ChevronRight
                      className="page-detail-layout__crumb-sep"
                      aria-hidden
                    />
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      )}
    </div>
  ) : undefined;

  // ─── Header actions: icon-only + filled/outlined buttons ─────────────────────────────────────
  const headerActions =
    actions && actions.length > 0 ? (
      <TooltipProvider delayDuration={200}>
        <div className="page-detail-layout__actions">
          {actions.map((action, i) => {
            const variant = mapMuiButtonVariant(action.variant, action.color);
            const isIconOnly = variant === 'ghost' && Boolean(action.icon);

            const actionSlug = action.label.toLowerCase().replace(/\s+/g, '-');
            const actionTestId = dataTestId ? `${dataTestId}-action-${actionSlug}` : undefined;

            if (isIconOnly) {
              return (
                <Tooltip key={i}>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="page-detail-layout__action-icon"
                      disabled={action.disabled}
                      onClick={action.onClick}
                      aria-label={action.label}
                      data-testid={actionTestId}
                    >
                      {action.icon}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{action.label}</TooltipContent>
                </Tooltip>
              );
            }

            return (
              <Button
                key={i}
                type="button"
                variant={variant}
                size="sm"
                className="gap-2"
                disabled={action.disabled}
                onClick={action.onClick}
                data-testid={actionTestId}
              >
                {action.icon}
                {action.label}
              </Button>
            );
          })}
        </div>
      </TooltipProvider>
    ) : undefined;

  // ─── Loading state ───────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="page-detail-layout page-detail-layout--loading" data-testid={dataTestId}>
        <PageDetailHeader title={title} subtitle={subtitle} leading={leading} />
        <PageContainer>
          <LoadingSkeleton rows={6} data-testid={dataTestId ? `${dataTestId}-loading` : undefined} />
        </PageContainer>
      </div>
    );
  }

  return (
    <div className="page-detail-layout" data-testid={dataTestId}>
      <PageDetailHeader
        title={title}
        subtitle={subtitle}
        leading={leading}
        actions={headerActions}
      />

      {/* Icon + status + chips + arbitrary extras. `PageDetailHeader` does not have a meta row of
          its own, so this is composed as its own strip immediately under the header — same visual
          order as before the migration (title → chips → extras). */}
      {(icon || status || (chips && chips.length > 0) || headerExtra) && (
        <div className="page-detail-layout__meta">
          {icon && <div className="page-detail-layout__icon">{icon}</div>}
          {status && (
            <StatusBadge status={status.label} color={mapMuiStatusColor(status.color)} size="sm" />
          )}
          {chips?.map((chip, i) => (
            <Chip
              key={`${chip.label}-${i}`}
              label={chip.label}
              color={mapMuiChipColor(chip.color)}
              variant={chip.variant === 'filled' ? 'filled' : 'outlined'}
            />
          ))}
          {headerExtra && <div className="page-detail-layout__extra">{headerExtra}</div>}
        </div>
      )}

      {/* Error surface — `InlineErrorPanel` has no dismiss slot of its own, so a small close button
          is rendered as the panel's `action` when a handler is supplied. Otherwise the panel is
          non-dismissible (retry lives on the page). */}
      {error && (
        <div className="page-detail-layout__error">
          <InlineErrorPanel
            message={error}
            data-testid={dataTestId ? `${dataTestId}-error` : undefined}
            action={
              onErrorDismiss ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onErrorDismiss}
                  aria-label="Dismiss error"
                >
                  <X className="h-4 w-4" aria-hidden />
                </Button>
              ) : undefined
            }
          />
        </div>
      )}

      <PageContainer>
        {tabs && tabs.length > 0 ? (
          <>
            <div className="page-detail-layout__tabs" role="tablist" aria-label="Page sections">
              {tabs.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  role="tab"
                  id={`page-detail-tab-${tab.value}`}
                  data-testid={dataTestId ? `${dataTestId}-tab-${tab.value}` : undefined}
                  aria-selected={activeTab === tab.value}
                  aria-controls={`page-detail-panel-${tab.value}`}
                  aria-disabled={tab.disabled || undefined}
                  disabled={tab.disabled}
                  className="page-detail-layout__tab"
                  onClick={handleTabChange(tab.value)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {tabs.map((tab) => (
              <div
                key={tab.value}
                role="tabpanel"
                id={`page-detail-panel-${tab.value}`}
                data-testid={dataTestId ? `${dataTestId}-panel-${tab.value}` : undefined}
                aria-labelledby={`page-detail-tab-${tab.value}`}
                hidden={activeTab !== tab.value}
                className="page-detail-layout__panel"
              >
                {/* Only render the active tab's content so heavy children (charts, editors) do not
                    mount on background tabs — same behaviour as the pre-migration `hidden` +
                    conditional-render pair. */}
                {activeTab === tab.value && tab.content}
              </div>
            ))}
          </>
        ) : (
          children
        )}
      </PageContainer>
    </div>
  );
};

export default PageDetailLayout;
